import { prop, mergeRight } from 'ramda';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { Provider } from 'react-redux';
import {
  SessionMiddleware, ThunkMiddleware, resourceReducer, loadTexts, loadCountries, IS_IFRAME,
} from '@twnel/web-components';
import {
  Api, Http, Endpoints, getApiHost,
} from '@twnel/utils-js/web';
import dataReducer from './data/reducer';
import { SessionSelector } from './data/selectors';
import { StorageHub } from './data/storage';
import { StorageFactory, appId, isLogoutRequest } from './data/util';
import { APP_INFO } from './data/constants';
import App from './ui';

(function main() {
  // Storage identifier. Vite only replaces the full `process.env.X` expression
  // and turns a bare `process.env` into `{}`, so these cannot be destructured.
  const debug = process.env.NODE_ENV !== 'production';
  const environment = getApiHost() || process.env.TWNEL_ENV;
  const identifier = appId({
    prefix: 'hub',
    version: process.env.packageVersion,
    environment,
    debug,
  });

  // iFrame version
  if (IS_IFRAME) {
    const hub = StorageHub({
      prefix: identifier,
      environment,
    });
    hub.listen();
    return;
  }

  // An app that logged out sends the user here to make sure the shared session is gone
  if (isLogoutRequest()) {
    StorageFactory(identifier).clear();
  }

  // TwnelApi
  const endpoints = Endpoints(environment);
  const api = Api(endpoints, Http());

  // Middleware set up.
  const session = SessionMiddleware({
    selector: SessionSelector(),
    storage: StorageFactory(identifier),
  });
  const initialState = {
    ...session.readState(),
    [APP_INFO]: {
      debug,
      environment,
      version: process.env.packageVersion,
      hubId: identifier,
    },
  };
  const thunk = ThunkMiddleware({ api });

  // Store set up
  const store = configureStore({
    reducer: combineReducers({ ...resourceReducer, ...dataReducer }),
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({
      thunk: false,
      serializableCheck: false,
    }).concat(thunk, session.middleware),
    preloadedState: initialState,
    devTools: debug,
  });

  // Load the texts file.
  store.dispatch(loadTexts(async (locale) => {
    let loaders;
    switch (locale) {
      case 'es':
        loaders = [
          import('@twnel/web-components/lib/es.json'),
          import('resources/localization/es.json'),
        ];
        break;
      case 'pt':
        loaders = [
          import('@twnel/web-components/lib/pt.json'),
          import('resources/localization/pt.json'),
        ];
        break;
      default:
        return {};
    }
    const result = await Promise.all(loaders);
    return result.map(prop('default')).reduce(mergeRight);
  }));

  // Load country list.
  store.dispatch(loadCountries(
    () => import('@twnel/web-components/lib/countries.json'),
  ));

  // Initial render.
  const app = React.createElement(App);
  createRoot(document.getElementById('react')).render(
    React.createElement(Provider, { store }, app),
  );
}());
